import { cn } from "@core/utils/cn";
import { useSidebarContext } from "@core/context";
import { forwardRef, type ComponentProps } from "react";
import type { IconType } from "react-icons";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "@core/components/UI/Icon";
import SideBarHeader from "./SideBarHeader";
import SideBarElement from "./SideBarElement";
import SideBarBtn from "./SideBarBtn";

type Link = {
  link?: string;
  icon: IconType | string;
  name: string;
  type: "link" | "button";
  role?: string[];
  action?: () => void;
  badge?: number | string;
};

export type MenuOption = {
  id: string;
  name: string;
  label?: string;
};

export type SideBarItem = {
  title: string;
  link: Link[];
  /** If true, this section is pinned to the bottom of the sidebar */
  footer?: boolean;
};

type UpgradeButton = {
  label: string;
  onClick: () => void;
};

type SideBarProps = ComponentProps<"aside"> & {
  items: SideBarItem[];
  userRole?: "Manager" | "Member";
  workspaceHeaderTitle: string;
  workspaceHeaderInitals?: string;
  workspaceHeadeBadgeTitle?: string;
  link?: string;
  backHome?: boolean;
  menuOptions?: MenuOption[];
  onWorkspaceSelect?: (workspaceName: string) => void;
  is_switch?: boolean;
  upgradeButton?: UpgradeButton;
};

const SideBar = forwardRef<HTMLDivElement, SideBarProps>(
  (
    {
      className,
      items,
      userRole,
      link,
      workspaceHeaderTitle,
      workspaceHeaderInitals,
      workspaceHeadeBadgeTitle,
      menuOptions,
      backHome = false,
      onWorkspaceSelect,
      is_switch = false,
      upgradeButton,
      ...props
    },
    ref,
  ) => {
    const { pathname } = useLocation();
    const { collapsed } = useSidebarContext();

    const safeDecode = (value?: string) => {
      if (!value) return "";
      try {
        return decodeURIComponent(value);
      } catch {
        return value;
      }
    };

    const normalizedPathname = safeDecode(pathname);

    const filterLinks = (item: SideBarItem) => ({
      ...item,
      link: item.link.filter((l) => {
        if (!l.role) return true;
        return userRole ? l.role.includes(userRole) : false;
      }),
    });

    const mainItems = items
      .filter((item) => !item.footer)
      .map(filterLinks)
      .filter((item) => item.link.length > 0);

    const footerItems = items
      .filter((item) => item.footer)
      .map(filterLinks)
      .filter((item) => item.link.length > 0);

    const renderSection = (item: SideBarItem, index: number) => (
      <div key={index} className="flex flex-col gap-2">
        {!collapsed && (
          <h6 className="text-xs text-neutral-500 font-normal capitalize px-2">
            {item.title}
          </h6>
        )}
        <ul
          className={cn(
            "flex flex-col w-full",
            collapsed ? "gap-2 items-center" : "gap-3",
          )}
        >
          {item.link.map((linkItem) => {
            const linkPath = linkItem.link || "";
            const normalizedLink = safeDecode(linkPath);
            const isActive =
              normalizedPathname === normalizedLink ||
              normalizedPathname.startsWith(normalizedLink + "/");

            return (
              <li
                key={linkItem.name}
                className={cn(collapsed ? "w-auto" : "w-full")}
              >
                {linkItem.type === "link" ? (
                  <SideBarElement
                    isActive={isActive}
                    to={linkPath}
                    icon={linkItem.icon}
                    text={linkItem.name}
                    badge={linkItem.badge}
                    collapsed={collapsed}
                  />
                ) : (
                  <SideBarBtn
                    icon={linkItem.icon}
                    text={linkItem.name}
                    action={linkItem.action}
                    collapsed={collapsed}
                  />
                )}
              </li>
            );
          })}
        </ul>
      </div>
    );

    return (
      <aside
        ref={ref}
        className={cn(
          "fixed left-0 top-0 h-screen flex flex-col border-r border-neutral-200 bg-white overflow-hidden",
          "md:w-[13rem] lg:w-[17.125rem]",
          className,
          "transition-[width] duration-200 ease-in-out",
          // Icon-only width wins across every breakpoint when collapsed
          collapsed && "w-[4.3125rem] md:w-[4.3125rem] lg:w-[4.3125rem]",
        )}
        {...props}
      >
        <SideBarHeader
          backHome={backHome}
          title={workspaceHeaderTitle}
          initials={workspaceHeaderInitals}
          badgeTitle={workspaceHeadeBadgeTitle}
          link={link}
          menuOptions={menuOptions}
          onWorkspaceSelect={onWorkspaceSelect}
          is_switch={is_switch}
          collapsed={collapsed}
        />

        {/* Body — top and footer sections separated */}
        <div
          className={cn(
            "flex-1 overflow-y-auto overflow-x-hidden flex flex-col justify-between py-5",
            collapsed ? "px-2" : "px-3",
          )}
        >
          {/* Top nav groups — gap 48px between sections */}
          <div className={cn("flex flex-col", collapsed ? "gap-4" : "gap-12")}>
            {/* Collapsed: back/return action moves into the body as a boxed icon */}
            {collapsed && backHome && (
              <Link
                to={link ?? "/"}
                aria-label="Return to home"
                className="mx-auto flex items-center justify-center w-9 h-9 rounded-lg border border-neutral-200 text-neutral-700 hover:text-neutral-950 hover:bg-surface-hover/50 transition-colors"
              >
                <Icon name="arrow-left" className="w-[18px] h-[18px]" />
              </Link>
            )}
            {mainItems.map(renderSection)}
          </div>

          {/* Footer nav groups + upgrade button — pinned to bottom */}
          {(footerItems.length > 0 || (upgradeButton && !collapsed)) && (
            <div className="flex flex-col gap-4 pt-6">
              {footerItems.map(renderSection)}
              {upgradeButton && !collapsed && (
                <button
                  type="button"
                  onClick={upgradeButton.onClick}
                  className="w-full flex items-center justify-center h-[2.5625rem] px-3 rounded-lg border border-neutral-200 bg-white text-sm font-medium text-secondary-900 hover:bg-surface-hover/50 transition-colors duration-200"
                >
                  {upgradeButton.label}
                </button>
              )}
            </div>
          )}
        </div>
      </aside>
    );
  },
);

SideBar.displayName = "SideBar";

export default SideBar;
